import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Upload } from "lucide-react";

export default function Branding() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const logoUrl = user?.tenant_has_logo
    ? `${process.env.REACT_APP_BACKEND_URL}/api/tenants/${user.tenant_id}/logo?v=${user.tenant_logo_version || 0}`
    : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tenants/${user.tenant_id}/logo`, fd);
      await refreshUser();
      toast.success("Logo updated");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/tenants/${user.tenant_id}/logo`);
      await refreshUser();
      toast.success("Logo removed");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="branding-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-zinc-900 mb-1">Branding</h1>
      <p className="text-sm text-zinc-500 mb-8">
        Manage the logo for <span className="font-semibold text-zinc-700">{user?.tenant_name || "your organisation"}</span>. It appears in the sidebar for everyone in your team.
      </p>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-6">
        <div className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-4">Tenant logo</div>
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 rounded-md border border-zinc-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl
              ? <img src={logoUrl} alt="logo" className="h-full w-full object-contain p-1.5" data-testid="branding-logo-preview" />
              : <ImageIcon size={26} className="text-zinc-300" />}
          </div>
          <div className="flex flex-col gap-2">
            <label data-testid="branding-logo-upload"
              className="inline-flex items-center gap-2 cursor-pointer bg-brand text-white rounded-full font-bold text-sm px-5 py-2.5 hover:bg-brand-dark transition-colors w-fit">
              <Upload size={16} /> {busy ? "Uploading…" : (logoUrl ? "Replace logo" : "Upload logo")}
              <input type="file" accept="image/*" className="hidden" disabled={busy}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
            </label>
            {logoUrl && (
              <button onClick={remove} disabled={busy} data-testid="branding-logo-remove"
                className="text-xs font-semibold text-red-600 hover:text-red-800 text-left">Remove logo</button>
            )}
            <span className="text-[11px] text-zinc-400">PNG, JPG, WEBP or SVG · up to 3MB</span>
          </div>
        </div>
      </div>
    </div>
  );
}
